import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RotateCw } from "lucide-react";
import { fetchActiveNetworks } from "../services/news";
import {
    createAdminPoll,
    fetchAdminPolls,
    markAdminPollsViewed,
} from "../services/polls";
import FilterPopover, { DateRangeFields } from "../components/FilterPopover";
import CollapsibleMobileCard from "../components/CollapsibleMobileCard";

function formatKyiv(dt) {
    if (!dt) return "";
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("uk-UA", {
        timeZone: "Europe/Kyiv",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).format(d).replace(",", "");
}

const AdminPolls = ({ onPollsViewed }) => {
    const [items, setItems] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const [filterFrom, setFilterFrom] = useState("");
    const [filterTo, setFilterTo] = useState("");

    const [newNetworkId, setNewNetworkId] = useState("");
    const [newQuestion, setNewQuestion] = useState("");
    const [newExpiresLocal, setNewExpiresLocal] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [polls, nets] = await Promise.all([
                fetchAdminPolls(),
                fetchActiveNetworks(),
            ]);
            setItems(Array.isArray(polls) ? polls : []);
            const active = Array.isArray(nets) ? nets.filter((n) => n && n.status) : [];
            setNetworks(active);

            if (!newNetworkId && active.length) {
                setNewNetworkId(String(active[0].id));
            }
        } catch (e) {
            setError(e?.message || "Помилка");
        } finally {
            setLoading(false);
        }
    }, [newNetworkId]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onChanged = () => load();
        window.addEventListener("wr_polls_changed", onChanged);
        return () => window.removeEventListener("wr_polls_changed", onChanged);
    }, [load]);

    useEffect(() => {
        (async () => {
            try {
                await markAdminPollsViewed();
                if (onPollsViewed) onPollsViewed();
            } catch (_) {
                // ignore
            }
        })();
    }, [onPollsViewed]);

    const onCreate = async () => {
        const network_id = parseInt(newNetworkId, 10);
        if (!Number.isFinite(network_id) || network_id <= 0) {
            setError("Оберіть мережу");
            return;
        }

        const question = (newQuestion || "").trim();
        if (question.length < 3) {
            setError("Питання занадто коротке");
            return;
        }

        if (!newExpiresLocal) {
            setError("Вкажіть кінцеву дату");
            return;
        }

        let expiresIso = null;
        try {
            const d = new Date(newExpiresLocal);
            if (Number.isNaN(d.getTime())) throw new Error();
            expiresIso = d.toISOString();
        } catch (_) {
            setError("Некоректна кінцева дата");
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            await createAdminPoll({ network_id, question, expires_at: expiresIso });
            setNewQuestion("");
            setNewExpiresLocal("");
            setShowCreateModal(false);
            await load();
        } catch (e) {
            setError(e?.message || "Не вдалося створити опитування");
        } finally {
            setSubmitting(false);
        }
    };

    const networkOptions = useMemo(() => {
        return (networks || [])
            .map((n) => ({ id: String(n.id), label: n.network_name || String(n.id) }))
            .sort((a, b) => a.label.localeCompare(b.label, "uk"));
    }, [networks]);

    const filteredItems = useMemo(() => {
        const fromMs = filterFrom ? new Date(`${filterFrom}T00:00:00`).getTime() : null;
        const toMs = filterTo ? new Date(`${filterTo}T23:59:59.999`).getTime() : null;
        const hasFrom = Number.isFinite(fromMs);
        const hasTo = Number.isFinite(toMs);

        if (!hasFrom && !hasTo) return items || [];

        return (items || []).filter((p) => {
            if (!p || !p.created_at) return true;
            const ms = new Date(p.created_at).getTime();
            if (!Number.isFinite(ms)) return true;
            if (hasFrom && ms < fromMs) return false;
            if (hasTo && ms > toMs) return false;
            return true;
        });
    }, [items, filterFrom, filterTo]);

    const filterActive = Boolean(filterFrom || filterTo);

    return (
        <div className="config-grid wr-polls-page">
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        className="add-btn toolbar-icon-btn"
                        type="button"
                        onClick={() => {
                            setError(null);
                            setShowCreateModal(true);
                        }}
                        title="Створити опитування"
                        aria-label="Створити опитування"
                    >
                        <Plus size={16} />
                    </button>
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        type="button"
                        onClick={load}
                        disabled={loading}
                        title="Оновити"
                        aria-label="Оновити"
                    >
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <FilterPopover
                        active={filterActive}
                        title="Період створення"
                        hint="Показати опитування за датою створення."
                        buttonTitle="Фільтр за датою"
                        onClear={() => {
                            setFilterFrom("");
                            setFilterTo("");
                        }}
                    >
                        <DateRangeFields
                            from={filterFrom}
                            to={filterTo}
                            onFromChange={setFilterFrom}
                            onToChange={setFilterTo}
                        />
                    </FilterPopover>
                </div>
            </div>

            {showCreateModal ? (
                <div className="modal-backdrop" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h3>Створити опитування</h3>
                        <select value={newNetworkId} onChange={(e) => setNewNetworkId(e.target.value)}>
                            {networkOptions.map((o) => (
                                <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                        </select>
                        <textarea
                            rows={3}
                            placeholder="Питання..."
                            value={newQuestion}
                            onChange={(e) => setNewQuestion(e.target.value)}
                        />
                        <input
                            type="datetime-local"
                            value={newExpiresLocal}
                            onChange={(e) => setNewExpiresLocal(e.target.value)}
                        />
                        {error ? <div className="message-banner">{error}</div> : null}
                        <div className="modal-actions">
                            <button className="add-btn" onClick={onCreate} disabled={submitting}>
                                {submitting ? "Створення..." : "Створити"}
                            </button>
                            <button
                                type="button"
                                className="add-btn cancel-btn"
                                onClick={() => setShowCreateModal(false)}
                                disabled={submitting}
                            >
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {loading ? <div>Завантаження...</div> : null}
            {!loading && (!Array.isArray(items) || items.length === 0) ? <div>Опитувань поки немає</div> : null}
            {!loading && Array.isArray(items) && items.length > 0 && (!Array.isArray(filteredItems) || filteredItems.length === 0) ? (
                <div>Нічого не знайдено за вибраний період</div>
            ) : null}

            <div className="config-grid">
                {(filteredItems || []).map((p) => {
                    const total =
                        Number(p?.total_clients_network || 0) + Number(p?.total_operators_network || 0);
                    const responded = Number(p?.responded_count || 0);
                    return (
                        <CollapsibleMobileCard
                            key={p.id}
                            expanded={expandedId === p.id}
                            onToggle={() => setExpandedId((cur) => (cur === p.id ? null : p.id))}
                            title={p.question}
                            meta={<span>{p.location_name ? p.location_name : "Вся мережа"}</span>}
                            preview={`До: ${formatKyiv(p.expires_at)} · Відповіли: ${responded}${total ? ` / ${total}` : ""}`}
                        >
                            <div><span className="collapsible-card__field-label">До</span>{formatKyiv(p.expires_at)}</div>
                            <div>
                                <span className="collapsible-card__field-label">Відповіли</span>
                                {responded}{total ? ` / ${total}` : ""}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Деталі</span>
                                Клієнтів: {Number(p?.responded_clients_count || 0)}/{Number(p?.total_clients_network || 0)}
                                {" · "}
                                Правління: {Number(p?.responded_board_count || 0)}/{Number(p?.total_operators_network || 0)}
                            </div>
                            <div>
                                <span className="collapsible-card__field-label">Голоси</span>
                                Так: {Number(p?.yes_count || 0)} · Ні: {Number(p?.no_count || 0)}
                            </div>
                        </CollapsibleMobileCard>
                    );
                })}
            </div>
        </div>
    );
};

export default AdminPolls;
